// Conteúdo da jornada de primeiro acesso: título, porquê, destino e tour de
// cada passo. O back devolve só id e estado — isto aqui é interface, e mudar
// uma vírgula não pode custar deploy do back.
//
// Os ids são o contrato entre os dois repos (SETUP_STEP_IDS / SELLER_STEP_IDS
// em back/src/features/onboarding-journey/schema.ts). A ordem também: é a
// ordem em que a tela lista os passos.
//
// Todo passo acionável tem um tour curto. O driver aponta para a ação que faz o
// back considerar o passo concluído; o estado continua vindo do servidor.
import type { LinkProps } from "@tanstack/react-router";
import {
  ClipboardCheck,
  Headphones,
  ListOrdered,
  MessagesSquare,
  PhoneOutgoing,
  SlidersHorizontal,
  UserPlus,
  Users,
  type LucideIcon,
} from "lucide-react";
import type { JourneyKind } from "@/api/onboarding";

export interface TourStepDef {
  // Valor de [data-tour="..."] na tela de destino. Ausente = balão centralizado.
  anchor?: string;
  // O elemento só entra no DOM depois que a pessoa abre um collapsible ou
  // accordion. O Driver espera a âncora aparecer antes de avançar.
  dynamic?: boolean;
  // O clique no elemento executa a ação da tela (abrir card/accordion) e já
  // avança para o próximo passo do tour.
  advanceOnClick?: boolean;
  title: string;
  text: string;
}

export interface JourneyStepDef {
  id: string;
  translationKey: string;
  title: string;
  why: string;
  icon: LucideIcon;
  // Ausente = passo bloqueado; não há para onde mandar a pessoa.
  to?: LinkProps["to"];
  tour: TourStepDef[];
}

export const JOURNEY_CATALOG: Record<JourneyKind, JourneyStepDef[]> = {
  setup: [
    {
      id: "stages",
      translationKey: "setupStages",
      title: "Monte as etapas da esteira",
      why: "A etapa decide a ordem da fila. Ajuste as padrão ou crie as suas.",
      icon: ListOrdered,
      to: "/management/queue-config",
      tour: [
        {
          anchor: "jornada-etapas",
          title: "Organize as etapas",
          text: "Aqui você define como as etapas do CRM viram status dentro da fila de ligação.",
        },
        {
          anchor: "jornada-etapas-lista",
          title: "Revise cada status",
          text: "Confira uma linha por vez: nome, etapas do CRM vinculadas, prioridade e se o status está ativo. Arraste para reorganizar e use o menu de três pontos para editar.",
        },
        {
          anchor: "jornada-etapas-preview",
          title: "Confira o resultado",
          text: "A prévia mostra como os leads serão ordenados com a configuração atual. Use-a para validar as prioridades antes de seguir.",
        },
        {
          anchor: "jornada-etapas-ajuda",
          title: "Entenda a prioridade",
          text: "Estas ajudas explicam de onde vem a prioridade e como a fila desempata leads com o mesmo peso.",
        },
        {
          anchor: "jornada-etapas-novo",
          title: "Crie ou complete uma etapa",
          text: "Ao criar um status, preencha nome, cor e prioridade, escolha o status padrão e avance para vincular todas as etapas correspondentes do CRM. Depois, salve.",
        },
      ],
    },
    {
      id: "first-lead",
      translationKey: "setupFirstLead",
      title: "Cadastre o primeiro lead",
      why: "Sem lead na base não há para quem ligar. Dá para cadastrar um ou subir uma planilha.",
      icon: UserPlus,
      to: "/management/leads",
      tour: [
        {
          anchor: "jornada-primeiro-lead",
          title: "Escolha como adicionar",
          text: "Você pode cadastrar um lead manualmente ou importar vários de uma planilha CSV.",
        },
        {
          anchor: "jornada-lead-manual",
          title: "Cadastre manualmente",
          text: "Use Novo lead para informar os dados da primeira pessoa. O telefone é essencial para ela entrar na operação.",
        },
        {
          anchor: "jornada-lead-importar",
          title: "Ou importe uma lista",
          text: "Se você já tem uma base, envie o CSV e confira o mapeamento das colunas antes de confirmar.",
        },
        {
          anchor: "jornada-lead-busca",
          title: "Confira o cadastro",
          text: "Depois de salvar, pesquise pelo nome ou telefone para confirmar que o lead entrou. A jornada será atualizada automaticamente.",
        },
      ],
    },
    {
      id: "queue-rule",
      translationKey: "setupQueueRule",
      title: "Defina a regra da fila",
      why: "Decide quem é chamado primeiro e quantas vezes.",
      icon: SlidersHorizontal,
      to: "/management/queue-config",
      tour: [
        {
          anchor: "fila-ajuda-ordem",
          title: "Entenda a ordem da fila",
          text: "A prioridade dos status define quem vem primeiro; esta ajuda explica os critérios de desempate.",
        },
        {
          anchor: "jornada-fila-distribuicao-abrir",
          advanceOnClick: true,
          title: "Abra a distribuição",
          text: "Clique neste cabeçalho para abrir o card e mostrar os operadores e percentuais que precisam ser configurados.",
        },
        {
          anchor: "jornada-fila-vendedores",
          dynamic: true,
          title: "Configure cada operador",
          text: "Em cada linha, arraste para definir a ordem e informe o percentual de leads. A distribuição é rebalanceada e precisa totalizar 100%.",
        },
        {
          anchor: "jornada-fila-distribuicao-salvar",
          dynamic: true,
          title: "Salve a distribuição",
          text: "Depois de revisar todos os operadores, salve a distribuição para aplicar os percentuais.",
        },
        {
          anchor: "jornada-fila-cadencia-abrir",
          advanceOnClick: true,
          title: "Abra as tentativas",
          text: "Clique neste cabeçalho para abrir o card e exibir as regras de tentativas de cada desfecho.",
        },
        {
          anchor: "jornada-fila-desfecho-no_answer",
          dynamic: true,
          advanceOnClick: true,
          title: "Sem resposta",
          text: "Clique para abrir. Defina quantas vezes tentar novamente quando ninguém atender.",
        },
        {
          anchor: "jornada-fila-desfecho-no_answer-campos",
          dynamic: true,
          title: "Cadência sem resposta",
          text: "Ajuste o máximo de chamadas e os minutos de espera após cada tentativa.",
        },
        {
          anchor: "jornada-fila-desfecho-busy",
          dynamic: true,
          advanceOnClick: true,
          title: "Linha ocupada",
          text: "Clique para abrir e configure uma nova tentativa mais rápida para chamadas ocupadas.",
        },
        {
          anchor: "jornada-fila-desfecho-busy-campos",
          dynamic: true,
          title: "Cadência de ocupado",
          text: "Defina o limite e os intervalos específicos para quando a linha estiver ocupada.",
        },
        {
          anchor: "jornada-fila-desfecho-voicemail",
          dynamic: true,
          advanceOnClick: true,
          title: "Caixa postal",
          text: "Clique para abrir e decidir quando tentar novamente após cair na caixa postal.",
        },
        {
          anchor: "jornada-fila-desfecho-voicemail-campos",
          dynamic: true,
          title: "Cadência de caixa postal",
          text: "Configure o número de chamadas e esperas mais longas para este desfecho.",
        },
        {
          anchor: "jornada-fila-desfecho-answered",
          dynamic: true,
          advanceOnClick: true,
          title: "Ligação atendida",
          text: "Clique para abrir e definir se um lead atendido pode voltar para novas tentativas.",
        },
        {
          anchor: "jornada-fila-desfecho-answered-campos",
          dynamic: true,
          title: "Cadência de atendida",
          text: "Ajuste o limite e o tempo de retorno conforme o processo comercial da operação.",
        },
        {
          anchor: "jornada-fila-desfecho-carrier",
          dynamic: true,
          advanceOnClick: true,
          title: "Falha de operadora",
          text: "Clique para abrir e configurar novas tentativas para falhas temporárias da operadora.",
        },
        {
          anchor: "jornada-fila-desfecho-carrier-campos",
          dynamic: true,
          title: "Cadência de operadora",
          text: "Defina quantas vezes repetir e quanto aguardar antes de tentar novamente.",
        },
        {
          anchor: "jornada-fila-cadencia-salvar",
          dynamic: true,
          title: "Salve a cadência",
          text: "Revise todos os desfechos e salve. Campos vazios impedem o salvamento para evitar retornos imediatos por engano.",
        },
        {
          anchor: "jornada-etapas-preview",
          title: "Valide a fila",
          text: "Confira na prévia se os leads aparecem na ordem esperada. Salve cada card alterado para concluir a configuração.",
        },
      ],
    },
    {
      id: "team",
      translationKey: "setupTeam",
      title: "Convide o time",
      why: "A ligação atendida precisa de alguém para atender do lado de cá.",
      icon: Users,
      to: "/management/users",
      tour: [
        {
          anchor: "jornada-time",
          title: "Escolha como trazer o time",
          text: "Crie o acesso diretamente ou envie um convite para a pessoa concluir o cadastro.",
        },
        {
          anchor: "jornada-time-criar",
          title: "Crie um usuário",
          text: "Informe nome, e-mail e papel quando quiser entregar um acesso já pronto.",
        },
        {
          anchor: "jornada-time-convidar",
          title: "Envie um convite",
          text: "Use o convite quando a própria pessoa for ativar a conta. Selecione o papel correto antes de enviar.",
        },
        {
          anchor: "jornada-time-lista",
          title: "Confirme o papel",
          text: "Confira na lista se a pessoa entrou e se o papel concede o acesso esperado. A jornada conclui quando houver outro membro na operação.",
        },
      ],
    },
    {
      id: "script",
      translationKey: "setupScript",
      title: "Escreva o roteiro",
      why: "O roteiro aparece ao lado do telefone durante a ligação.",
      icon: Headphones,
      to: "/management/sales-script",
      tour: [
        {
          anchor: "jornada-roteiro",
          title: "Estruture a conversa",
          text: "O roteiro aparece para o operador durante a ligação. Organize os tópicos na ordem em que devem ser abordados.",
        },
        {
          anchor: "jornada-roteiro-editor",
          title: "Escreva cada tópico",
          text: "Use textos curtos e objetivos. Você pode editar, remover e arrastar os tópicos para mudar a sequência.",
        },
        {
          anchor: "jornada-roteiro-adicionar",
          title: "Adicione o que estiver faltando",
          text: "Crie tópicos para abertura, diagnóstico, proposta e próximo passo até cobrir o fluxo da chamada.",
        },
        {
          anchor: "jornada-roteiro-salvar",
          title: "Salve o roteiro",
          text: "Revise a ordem e salve. A jornada só considera esta etapa concluída depois que a configuração for gravada.",
        },
      ],
    },
    {
      id: "objections",
      translationKey: "setupObjections",
      title: "Prepare as objeções",
      why: "O que responder quando o cliente empaca. Também aparece durante a ligação.",
      icon: MessagesSquare,
      to: "/management/objections",
      tour: [
        {
          anchor: "jornada-objecoes",
          title: "Prepare as respostas",
          text: "As objeções ficam disponíveis durante a ligação para o operador responder com segurança.",
        },
        {
          anchor: "jornada-objecoes-editor",
          title: "Preencha pergunta e resposta",
          text: "Em cada item, escreva o que o cliente costuma dizer e a resposta recomendada para contornar a dúvida.",
        },
        {
          anchor: "jornada-objecoes-adicionar",
          title: "Cubra os casos mais comuns",
          text: "Adicione preço, prazo, concorrência, falta de interesse e outras objeções recorrentes da sua operação.",
        },
        {
          anchor: "jornada-objecoes-salvar",
          title: "Salve as objeções",
          text: "Revise os pares e salve. A jornada será atualizada quando a configuração estiver gravada.",
        },
      ],
    },
    {
      id: "first-call",
      translationKey: "setupFirstCall",
      title: "Faça a primeira ligação",
      why: "É o teste de ponta a ponta: se a ligação sai, a telefonia da marca está funcionando. Se travar, o discador avisa o que falta.",
      icon: PhoneOutgoing,
      to: "/dialer",
      tour: [
        {
          anchor: "jornada-primeira-ligacao",
          title: "Conheça o discador",
          text: "Aqui você atende o lead atual, consulta o roteiro e acompanha os próximos contatos da fila.",
        },
        {
          anchor: "dialer-disponibilidade",
          title: "Fique disponível",
          text: "Ative sua disponibilidade para participar da fila. O telefone precisa estar conectado para iniciar.",
        },
        {
          anchor: "jornada-ligacao-iniciar",
          title: "Inicie a sessão",
          text: "O botão Iniciar busca o primeiro lead elegível e começa o fluxo de chamadas.",
        },
        {
          anchor: "jornada-ligacao-guia",
          title: "Use o apoio da ligação",
          text: "Consulte o roteiro e as objeções enquanto conversa, sem sair do discador.",
        },
        {
          anchor: "jornada-ligacao-fila",
          title: "Acompanhe a fila",
          text: "Veja quem vem depois e o andamento da sessão. A etapa termina após uma ligação ser realmente realizada.",
        },
      ],
    },
  ],
  seller: [
    {
      id: "first-call",
      translationKey: "sellerFirstCall",
      title: "Atenda a sua primeira ligação",
      why: "Você só recebe chamada já atendida. O discador faz a discagem por você.",
      icon: PhoneOutgoing,
      to: "/dialer",
      tour: [
        {
          anchor: "dialer-disponibilidade",
          title: "Ligue a sua disponibilidade",
          text: "Enquanto esta chave estiver desligada, a fila não entrega chamadas. Ative-a com o telefone conectado.",
        },
        {
          anchor: "jornada-ligacao-iniciar",
          title: "Comece a sessão",
          text: "Inicie para receber o primeiro lead. Depois disso, o discador conduz o avanço da fila.",
        },
        {
          anchor: "jornada-ligacao-guia",
          title: "Acompanhe o roteiro",
          text: "Durante a conversa, use o roteiro e as respostas de objeções preparados pela gestão.",
        },
        {
          anchor: "jornada-ligacao-fila",
          title: "Aguarde a próxima chamada",
          text: "A fila mostra os próximos leads. Continue disponível para receber as chamadas automaticamente.",
        },
      ],
    },
    {
      id: "first-outcome",
      translationKey: "sellerFirstOutcome",
      title: "Tabule a ligação",
      why: "O desfecho fica registrado no lead e decide quando ele é chamado de novo.",
      icon: ClipboardCheck,
      to: "/dialer",
      tour: [
        {
          anchor: "jornada-tabulacao",
          title: "Conclua o atendimento",
          text: "Faça a ligação normalmente. Ao terminar, a telefonia identifica e registra o desfecho automaticamente.",
        },
        {
          anchor: "jornada-ligacao-lead",
          title: "Confira o lead atual",
          text: "O card mostra tentativas e o último desfecho. Essas informações ajudam a retomar o contexto antes de falar.",
        },
        {
          anchor: "jornada-ligacao-fila",
          title: "Acompanhe o avanço",
          text: "Depois que o resultado for salvo, o lead avança conforme a cadência e o próximo contato é servido.",
        },
        {
          anchor: "jornada-primeira-ligacao",
          title: "Mantenha a sessão ativa",
          text: "Continue no discador até a confirmação do resultado. A jornada conclui quando o primeiro desfecho estiver registrado.",
        },
      ],
    },
  ],
};
